import axios, { type AxiosResponse } from 'axios'
import type { BackendErrorPayload } from 'src/utils/error.utils'

export type QueryError = BackendErrorPayload<string> & {
  status?: number
  originalError?: unknown
}

const withOriginalError = (
  payload: QueryError,
  originalError: unknown,
): QueryError => {
  if ('originalError' in payload) {
    return payload
  }

  return Object.defineProperty(payload, 'originalError', {
    configurable: true,
    enumerable: false,
    value: originalError,
  })
}

export const normalizeQueryError = (error: unknown): QueryError => {
  const isAxiosError =
    axios.isAxiosError<BackendErrorPayload<string>>(error) ||
    (error &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      error.isAxiosError === true)

  if (isAxiosError) {
    const axiosError = error as {
      message: string
      response?: {
        data?: BackendErrorPayload<string>
        status?: number
        statusText?: string
      }
    }
    const responseData = axiosError.response?.data

    if (
      responseData &&
      typeof responseData === 'object' &&
      'error' in responseData
    ) {
      return withOriginalError(
        {
          ...responseData,
          status: axiosError.response?.status,
        },
        error,
      )
    }

    return withOriginalError(
      {
        error: axiosError.response?.statusText ?? axiosError.message,
        status: axiosError.response?.status,
      },
      error,
    )
  }

  if (error && typeof error === 'object' && 'error' in error) {
    return withOriginalError(error as QueryError, error)
  }

  if (error instanceof Error) {
    return withOriginalError({ error: error.message }, error)
  }

  if (typeof error === 'string') {
    return { error }
  }

  return { error: 'Sorry, something went wrong. Please try again later.' }
}

export const getResponseData = async <T>(
  request: Promise<AxiosResponse<T>>,
): Promise<T> => {
  try {
    const response = await request
    return response.data
  } catch (error) {
    throw normalizeQueryError(error)
  }
}
